import { isNumber } from 'lodash'

import type { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'

// Source: https://github.com/vinted/dwh-schema-registry/blob/5387d2ce7b44ee93711a15dfb97c60a4d984f22c/avro/events/common/shared/user.avdl#L25
type UserTarget =
  | 'read_more'
  | 'filter_by_brand'
  | 'favorite'
  | 'breadcrumb'
  | 'image_thumbnail'
  | 'image_thumbnail_modal'
  | 'image_modal'

// https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/common/shared/common.avdl#L34
export type ContentSource =
  | 'followed_user_items'
  | 'followed_brand_items'
  | 'vinted_picks'
  | 'promo'
  | 'video_items'
  | 'nearby_items'
  | 'marketing_items'
  | 'new_user_catalog_item'
  | 'new_user_popular_item'
  | 'new_user_sold_item'
  | 'catalog'
  | 'favorite_items'
  | 'user_items'
  | 'marketing_page'
  | 'item_group'
  | 'similar_items'
  | 'other_user_items'
  | 'editorial'
  | 'bundle'
  | 'brand_suggestion'
  | 'promoted_items'
  | 'new_upload'
  | 'new_user_upload'
  | 'new_seller_upload'
  | 'recommended_seller_upload'
  | 'promoted_members'
  | 'promoted_closets'
  | 'promoted_closets_preview'
  | 'find_friends'
  | 'user_discovery'
  | 'news_feed'
  | 'item_collection'
  | 'search'
  | 'catalog_promoted_items'
  | 'search_promoted_items'
  | 'recommended_items'
  | 'random_items'
  | 'canonical_items'
  | 'popular_items'
  | 'popular_promoted_items'
  | 'query_recommended_items'
  | 'favorite_promoted_items'
  | 'items_based_on_recent_purchases'
  | 'personalized_promoted_items'
  | 'lister_activation_banner'
  | 'verified_items'
  | 'verified_promoted_items'
  | 'catalog_promoted_closets'
  | 'search_promoted_closets'
  | 'catalog_ad'
  | 'search_ad'
  | 'feed_ad'
  | 'featured_collection'
  | 'gallery_image'
  | 'gallery_ad'
  | 'recommended_category_items'
  | 'galleried_items'
  | 'price_range_brand_items'
  | 'followed_users'
  | 'price_range_category_items'
  | 'seasonal_thumbnails'
  | 'recently_viewed_items'
  | 'complementary_items'
  | 'saved_search_items'
  | 'hvf_items'
  | 'hvf_promoted_items'
  | 'accessories_banner'
  | 'search_by_image'
  | 'hvf_banner'
  | 'ivs_banner'
  | 'homepage_block'
  | 'dac7_banner'
  | 'spring_cleaning_banner'
  | 'edit_price'
  | 'designer_recommended_items'
  | 'designer_items_based_on_recent_purchases'
  | 'designer_shop_by_brand'
  | 'designer_new_item_upload'
  | 'designer_ivs_banner'
  | 'electronics_new_item_upload'
  | 'electronics_items_by_categories'
  | 'electronics_recommended_items'
  | 'electronics_recommended_brand_items'
  | 'electronics_suggested_categories'
  | 'electronics_video_games_and_consoles_banner'
  | 'electronics_mobile_phones_and_tablets_banner'
  | 'video_games_and_consoles_banner'
  | 'mobile_phones_and_tablets_banner'
  | 'more_similar_items'
  | 'electronics_general_banner'
  | 'electronics_highlights_banner'
  | 'electronics_phones_and_tablets_banner'
  | 'electronics_laptops_banner'
  | 'collectibles_banner'
  | 'high_value_electronics_banner'
  | 'promoted_category_items'
  | 'feed_women_recommended_items'
  | 'feed_men_recommended_items'
  | 'feed_kids_recommended_items'
  | 'feed_entertainment_recommended_items'
  | 'feed_home_recommended_items'
  | 'feed_pet_care_recommended_items'
  | 'feed_sports_recommended_items'
  | 'feed_hobbies_collectables_recommended_items'
  | 'feed_women_new_items'
  | 'feed_men_new_items'
  | 'feed_kids_new_items'
  | 'feed_entertainment_new_items'
  | 'feed_home_new_items'
  | 'feed_pet_care_new_items'
  | 'feed_sports_new_items'
  | 'feed_hobbies_collectables_new_items'

// Source: https://github.com/vinted/dwh-schema-registry/blob/7726a66903b34c91df67e51284bcf1f60cdbefae/avro/events/common/shared/common.avdl#L34
type ContentTypes =
  | 'item'
  | 'user'
  | 'brand'
  | 'matching_brand_suggestion'
  | 'promo'
  | 'ad'
  | 'native_ad'
  | 'promoted_user'
  | 'promoted_closet'
  | 'lister_activation_banner'
  | 'featured_collection'
  | 'followed_user_block'

type UserViewItemGalleryEventArgs = {
  imagePosition: number
  imageCount: number
  itemId: number
  isLoop: boolean
  contentType: string
  galleryContainsAd: boolean
  imageView: 'fullscreen' | 'item_page'
}

type UserViewItemGalleryEventExtra = {
  image_position: number
  number_item_images: number
  item_id: number
  image_view: 'fullscreen' | 'item_page'
  is_loop: boolean
  content_type: string
  gallery_contains_ad: boolean
}

export const userViewItemGallery = (args: UserViewItemGalleryEventArgs): TrackingEvent => {
  const { imagePosition, imageCount, itemId, isLoop, contentType, galleryContainsAd, imageView } =
    args

  const extra: UserViewItemGalleryEventExtra = {
    // This value is hardcoded, because we have gallery only in a modal form
    // hence the `fullscreen` name. In apps, they have two modes to show gallery,
    // in overlay form or in the page itself, hence they have different values here.
    image_view: imageView,
    image_position: imagePosition,
    number_item_images: imageCount,
    item_id: itemId,
    is_loop: isLoop,
    content_type: contentType,
    gallery_contains_ad: galleryContainsAd,
  }

  return {
    event: 'user.view_item_gallery',
    extra,
  }
}

type ViewItemArgs = {
  itemId: number
  ownerId: number
  referrerPath?: string
  searchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
}

export const viewItemEvent = (args: ViewItemArgs) => {
  const { itemId, ownerId, referrerPath, searchSessionId, globalCatalogBrowseSessionId } = args

  return {
    event: 'user.view_item',
    extra: {
      item_id: itemId,
      item_owner_id: ownerId,
      path: referrerPath,
      search_session: searchSessionId,
      global_catalog_browse_session_id: globalCatalogBrowseSessionId || undefined,
    },
  }
}

type ViewOwnItemArgs = {
  itemId: number
  ownerId: number
  referrerPath?: string
  searchSessionId: string | null
}

export const viewOwnItemEvent = (args: ViewOwnItemArgs) => {
  const { itemId, ownerId, referrerPath, searchSessionId } = args

  return {
    event: 'user.view_current_user_item',
    extra: {
      item_id: itemId,
      item_owner_id: ownerId,
      path: referrerPath,
      search_session: searchSessionId,
    },
  }
}

type ItemAttributesEventArgs = {
  itemId: string
  attributeCode: string
  attributeValueId?: string
  details?: string
  itemPageSessionId?: string
}

type ItemAttributesEventExtra = {
  screen: string
  item_id: string
  attribute_code: string
  attribute_value_id?: string | null
  details?: string | null
  item_page_session_id?: string | null
}

export const itemAttributesViewEvent = (args: ItemAttributesEventArgs) => {
  const { itemId, attributeCode, attributeValueId, details, itemPageSessionId } = args

  const extra: ItemAttributesEventExtra = {
    screen: 'item',
    item_id: itemId,
    attribute_code: attributeCode,
  }

  if (attributeValueId) extra.attribute_value_id = attributeValueId
  if (details) extra.details = details
  if (itemPageSessionId) extra.item_page_session_id = itemPageSessionId

  return {
    event: 'item_page.item_attributes.view',
    extra,
  }
}

type ItemPageChannelViewEventArgs = {
  contentSource: string
  itemId: string
}

type ItemPageChannelViewEventExtra = {
  content_source: string
  item_id: string
}

export const itemPageChannelViewEvent = (args: ItemPageChannelViewEventArgs) => {
  const { contentSource, itemId } = args

  const extra: ItemPageChannelViewEventExtra = {
    content_source: contentSource,
    item_id: itemId,
  }

  return {
    event: 'item_page.channel.view',
    extra,
  }
}

type ItemPageChannelClickEventArgs = {
  target: 'horizontal_items_block_header_cta'
  contentSource: string
  itemId: string
}

type ItemPageChannelClickEventExtra = {
  target: 'horizontal_items_block_header_cta'
  content_source: string
  item_id: string
}

export const itemPageChannelClickEvent = (args: ItemPageChannelClickEventArgs) => {
  const { target, contentSource, itemId } = args

  const extra: ItemPageChannelClickEventExtra = {
    target,
    content_source: contentSource,
    item_id: itemId,
  }

  return {
    event: 'item_page.channel.click',
    extra,
  }
}

type ClickEventArgs = {
  target: UserTarget
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: UserTarget
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

type FavouriteItemEventArgs = {
  itemId: number
  isFollowEvent: boolean
  contentSource?: ContentSource | null
  searchCorrelationId?: string
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  itemOwnerId?: string | number | null
}

type FavouriteItemEventExtra = {
  item_id: number
  content_source?: ContentSource
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  homepage_session_id?: string
  item_owner_id?: string | number
}

export const favouriteItemEvent = ({
  itemId,
  isFollowEvent,
  contentSource,
  searchCorrelationId,
  searchSessionId,
  globalSearchSessionId,
  globalCatalogBrowseSessionId,
  homepageSessionId,
  itemOwnerId,
}: FavouriteItemEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.favorite_item' : 'user.unfavorite_item'
  const extra: FavouriteItemEventExtra = { item_id: itemId }

  if (contentSource) extra.content_source = contentSource
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (itemOwnerId) extra.item_owner_id = itemOwnerId

  return {
    event: eventName,
    extra,
  }
}

type ImpressionEventArgs = {
  id: number | string
  contentType: ContentTypes
  contentSource: ContentSource
  position: number
  itemDistance?: number | null
  itemOwnerId?: string | number | null
  searchScore?: string
  searchSignals?: Array<string> | null
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  uploadSessionId?: string
  screen?: string
  metadata?: Record<string, Array<string>>
}

type ImpressionEventExtra = {
  content_type: ContentTypes
  content_source: ContentSource
  id: number | string
  position: number
  item_owner_id?: string | number
  search_score?: string
  search_signals?: Array<string>
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  buyer_item_distance_km?: number
  homepage_session_id?: string
  upload_session_id?: string
  screen?: string
  metadata?: Record<string, Array<string>>
}

export const impressionEvent = (args: ImpressionEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    itemDistance,
    itemOwnerId,
    position,
    searchScore,
    searchSignals,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    uploadSessionId,
    screen,
    metadata,
  } = args

  const extra: ImpressionEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (itemOwnerId) extra.item_owner_id = itemOwnerId
  if (searchScore) extra.search_score = JSON.stringify(searchScore)
  if (searchSignals) extra.search_signals = searchSignals
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (uploadSessionId) extra.upload_session_id = uploadSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'list.show_item',
    extra,
  }
}

type ProfileClickTarget =
  | 'user_short_info_cell'
  | 'itembox_user_profile_link'
  | 'user_profile_link'
  | 'view_all_user_items'
  | 'seller_feedback_title'
  | 'seller_feedback_photo'
  | 'view_all_user_items_card_cta'
  | 'view_all_user_items_title_cta'

type ProfileClickEventArgs = {
  target: ProfileClickTarget
  ownerId: string
  screen?: string
  itemId?: string
}

type ProfileClickEventExtra = {
  target: ProfileClickTarget
  owner_id: string
  screen?: string
  item_id?: string
}

export const profileClickEvent = (args: ProfileClickEventArgs): TrackingEvent => {
  const { target, ownerId, screen, itemId } = args

  const extra: ProfileClickEventExtra = { target, owner_id: ownerId }

  if (screen) extra.screen = screen
  if (itemId) extra.item_id = itemId

  return {
    event: 'profile.click',
    extra,
  }
}

export const itemAttributesClickEvent = (args: ItemAttributesEventArgs) => {
  const { itemId, attributeCode, attributeValueId, details, itemPageSessionId } = args

  const extra: ItemAttributesEventExtra = {
    screen: 'item',
    item_id: itemId,
    attribute_code: attributeCode,
  }

  if (attributeValueId) extra.attribute_value_id = attributeValueId
  if (details) extra.details = details
  if (itemPageSessionId) extra.item_page_session_id = itemPageSessionId

  return {
    event: 'item_page.item_attributes.click',
    extra,
  }
}

type ItemPageViewEventExtra = {
  target: string
  item_id: string
  details?: string
  item_page_session_id?: string
}

export const itemPageViewEvent = (args: ItemPageViewEventExtra) => {
  const { target, item_id, details, item_page_session_id } = args

  const extra: ItemPageViewEventExtra = {
    target,
    item_id,
  }

  if (details) extra.details = details
  if (item_page_session_id) extra.item_page_session_id = item_page_session_id

  return {
    event: 'item_page.view',
    extra,
  }
}
